// contexts/produtos/combos/ProdutoConjuntoContext.js
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAlert } from '../../geral/AlertContext';
import { useMessage } from '../../geral/MessageContext';
import {
    adicionarProdutoConjunto,
    alterarProdutoConjunto,
    buscarConjuntosDetalhados,
    excluirProdutoConjunto
} from '../../../services/produtos/ProdutoConjuntoService';
import { useConjunto } from './ConjuntoContext';

const ProdutoConjuntoContext = createContext();

export const useProdutoConjunto = () => useContext(ProdutoConjuntoContext);

const filtrarConjuntosPorProduto = (lista, produtoId) => {
    const produtoIdNumber = Number(produtoId);
    if (!produtoIdNumber) return [];

    return (lista || []).filter((item) => Number(item.produtoId) === produtoIdNumber);
};

export const ProdutoConjuntoProvider = ({ children }) => {
    const [produtoConjuntosDetalhado, setProdutoConjuntosDetalhadoState] = useState([]);
    const [produtoConjuntoVinculos, setProdutoConjuntoVinculos] = useState([]);
    const [produtoConjuntoProdutoId, setProdutoConjuntoProdutoId] = useState(null);
    const { showAlert } = useAlert();
    const { showMessage } = useMessage();
    const { conjuntos } = useConjunto();
    const conjuntosPorId = useMemo(
        () => new Map((conjuntos || []).map((conjunto) => [Number(conjunto.id), conjunto])),
        [conjuntos]
    );
    const produtoConjuntos = useMemo(
        () => produtoConjuntoVinculos.map((vinculo) => ({
            ...vinculo,
            conjunto: conjuntosPorId.get(Number(vinculo.conjuntoId)) || null
        })),
        [conjuntosPorId, produtoConjuntoVinculos]
    );

    // Fetch inicial
    useEffect(() => {
        const fetchDataFromAPI = async () => {
            try {
                // O sistema traz os vínculos uma vez e usa os conjuntos já carregados pelo ConjuntoContext.
                console.log("Buscando os conjuntos detalhados");
                const result = await buscarConjuntosDetalhados();
                setProdutoConjuntoVinculos(result || []);
                console.log("Buscou os conjuntos detalhados");
            } catch (error) {
                console.error("Erro ao buscar ProdutoConjunto:", error);
                showAlert('Erro ao buscar ProdutoConjunto!', 'danger');
            }
        };
        fetchDataFromAPI();
    }, []);

    useEffect(() => {
        setProdutoConjuntosDetalhadoState(
            filtrarConjuntosPorProduto(produtoConjuntos, produtoConjuntoProdutoId)
        );
    }, [produtoConjuntos, produtoConjuntoProdutoId]);

    const setProdutoConjuntosDetalhado = (value) => {
        if (Array.isArray(value) && value.length === 0) {
            setProdutoConjuntoProdutoId(null);
        }

        setProdutoConjuntosDetalhadoState(value);
    };

    const fetchProdutoConjuntosDetalhado = (produtoId) => {
        if (!produtoId) {
            setProdutoConjuntoProdutoId(null);
            setProdutoConjuntosDetalhadoState([]);
            return;
        }

        setProdutoConjuntoProdutoId(produtoId);
        setProdutoConjuntosDetalhadoState(filtrarConjuntosPorProduto(produtoConjuntos, produtoId));
    };


    const addProdutoConjunto = async (item) => {
        try {
            const result = await adicionarProdutoConjunto(item);
            setProdutoConjuntoVinculos((prev) => [...prev, result]);
            showAlert('Conjunto adicionado ao produto com sucesso!');
        } catch (error) {
            console.error("Erro ao adicionar conjunto ao produto:", error);
            showMessage('Erro ao adicionar conjunto ao produto!\n' + error, 'danger');
        }
    };

    const updateProdutoConjunto = async (item) => {
        try {
            const result = await alterarProdutoConjunto(item);
            setProdutoConjuntoVinculos((prev) =>
                prev.map((i) => (i.id === result.id ? result : i))
            );
            showAlert('Conjunto do produto alterado com sucesso!');
        } catch (error) {
            console.error("Erro ao atualizar conjunto do produto:", error);
            showAlert('Erro ao alterar conjunto do produto!', 'danger');
        }
    };

    const deleteProdutoConjunto = async (id) => {
        try {
            await excluirProdutoConjunto(id);
            setProdutoConjuntoVinculos((prev) => prev.filter(i => i.id !== id));
            showAlert('Conjunto do produto excluído com sucesso!');
        } catch (error) {
            console.error("Erro ao excluir conjunto do produto:", error);
            showAlert('Erro ao excluir conjunto do produto!', 'danger');
        }
    };

    return (
        <ProdutoConjuntoContext.Provider value={{
            produtoConjuntos,
            produtoConjuntosDetalhado,
            setProdutoConjuntosDetalhado,
            addProdutoConjunto,
            updateProdutoConjunto,
            deleteProdutoConjunto,
            fetchProdutoConjuntosDetalhado
        }}>
            {children}
        </ProdutoConjuntoContext.Provider>
    );
};
